/**
 * Shared tracking event definitions for ad platform pixel tracking
 * (Meta, TikTok, Google, X). Used by both frontend (browser pixels)
 * and backend (Conversions API / CAPI).
 */

export interface CompleteRegistrationEventData {
  content_name: string;
  status: string;
}

export interface StartTrialEventData {
  content_name: string;
  currency: string;
  value: number;
  predicted_ltv: number;
}

export interface PaidSubEventData {
  content_name: string;
  currency: string;
  value: number;
  predicted_ltv: number;
}

/**
 * A paid subscription cycle after the first. Server-only: it comes from the
 * Stripe renewal webhook, so no browser pixel ever fires it.
 */
export interface RenewalEventData {
  content_name: string;
  currency: string;
  value: number;
}

/** A move to a higher plan or an added add-on. Server-only, like Renewal. */
export interface UpgradeEventData {
  content_name: string;
  currency: string;
  value: number;
}

export interface TrackingEventMap {
  CompleteRegistration: CompleteRegistrationEventData;
  StartTrial: StartTrialEventData;
  PaidSub: PaidSubEventData;
  Renewal: RenewalEventData;
  Upgrade: UpgradeEventData;
}

export type TrackingEventName = keyof TrackingEventMap;

export type TrackingEventData<T extends TrackingEventName> =
  TrackingEventMap[T];

export enum PixelPlatform {
  Meta = 'mp',
  TikTok = 'tp',
  Google = 'gt',
  X = 'xp',
  LinkedIn = 'li',
}

export const TrackingEvent: TrackingEventMap = {
  CompleteRegistration: {
    content_name: 'creator_signup',
    status: 'complete',
  },
  StartTrial: {
    content_name: 'pro_trial',
    currency: 'USD',
    value: 0,
    predicted_ltv: 0,
  },
  PaidSub: {
    content_name: 'paid_subscription',
    currency: 'USD',
    value: 0,
    predicted_ltv: 0,
  },
  Renewal: {
    content_name: 'paid_subscription',
    currency: 'USD',
    value: 0,
  },
  Upgrade: {
    content_name: 'paid_subscription',
    currency: 'USD',
    value: 0,
  },
};

// X (Twitter) conversion event IDs from X Ads Manager — static per event type.
// Used by both frontend browser pixel (twq) and backend CAPI.
// An empty ID is the documented "not set up on X's side" state: every X call
// site returns early on it, so the other platforms still fire.
export const XPixelEventId: Record<TrackingEventName, string> = {
  CompleteRegistration: 'tw-r4kvz-rc0jg',
  StartTrial: 'tw-r4kvz-rc0jh',
  PaidSub: '',
  Renewal: '',
  Upgrade: '',
};

// LinkedIn conversion IDs from LinkedIn Campaign Manager — the numeric tail of
// the conversion URN (`urn:lla:llaPartnerConversion:<id>`). The browser Insight
// Tag takes the number; the backend CAPI takes the full URN, built from it by
// `linkedInConversionUrn`. `null` = not created in Campaign Manager yet, and
// every LinkedIn call site returns early on it.
//
// These are public identifiers (the browser ships them), not secrets. The
// backend reads the same conversions from its OWN Firestore config
// (`linkedIn.conversionApi.conversionUrns`), so a value changed in Campaign
// Manager has to be updated in BOTH places or the two halves of one conversion
// report against different rules.
export const LinkedInConversionId: Record<TrackingEventName, number | null> = {
  CompleteRegistration: 27505105,
  StartTrial: 27646201,
  PaidSub: 30572913,
  Renewal: null,
  Upgrade: null,
};

export const linkedInConversionUrn = (id: number) =>
  `urn:lla:llaPartnerConversion:${id}`;

// Google Ads conversion labels from Google Ads → Goals → Conversions — the part
// after the `/` in a snippet's `send_to: 'AW-<account>/<label>'`. The account
// id is per environment and lives in the web app's config (`googleAdsId`), so
// the browser builds `send_to` from both. Browser-only: there is no
// server-side Google Ads upload, so the backend never reads this table.
// An empty label = no conversion action in Google Ads, and the Google tag
// sends no Ads conversion for that event (its GA4 event still goes out).
export const GoogleAdsConversionLabel: Record<TrackingEventName, string> = {
  CompleteRegistration: 'P0M0CI2Y9ZsdEOrrgpYD',
  StartTrial: 'beRXCMCu75sdEOrrgpYD',
  PaidSub: 'BCvTCKjl-JsdEOrrgpYD',
  Renewal: '',
  Upgrade: '',
};
